import type { CardData } from '@/contexts/CardContext';
import { formatAmount, getUnitSymbol } from '@/lib/cashu';
import { BACK_COVER_URL, REDEEM_IMAGE_URL } from '@/lib/presetImages';

interface CardPreviewProps {
  card: CardData;
  side?: 'outside' | 'inside' | 'both';
}

/**
 * Foldable A7 card preview.
 * Outside: fixed back cover (left) + user front cover (right)
 * Inside:  QR page (left) + user inside image with message (right)
 */
export function CardPreview({ card, side = 'both' }: CardPreviewProps) {
  if (side === 'outside') return <OutsidePreview card={card} />;
  if (side === 'inside') return <InsidePreview card={card} />;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest text-center">Outside (fold in half &rarr;)</p>
        <OutsidePreview card={card} />
      </div>
      <div className="space-y-2">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest text-center">Inside (open card)</p>
        <InsidePreview card={card} />
      </div>
    </div>
  );
}

/** Outside: fixed back cover (left, rotated) + user front cover (right) */
function OutsidePreview({ card }: { card: CardData }) {
  return (
    <div className="w-full max-w-[480px] mx-auto aspect-[10/7] flex rounded-xl overflow-hidden shadow-lg border border-border/50">
      {/* Left = Back cover (always the fixed branding, rotated 180deg) */}
      <div className="w-1/2 h-full relative bg-white flex items-start justify-center pt-[4%]" style={{ transform: 'rotate(180deg)' }}>
        <img src={BACK_COVER_URL} alt="Back cover" className="object-contain" style={{ width: '40%', height: 'auto', transform: 'rotate(180deg)' }} />
      </div>
      {/* Fold line */}
      <div className="w-px bg-border/80 relative z-10 flex-shrink-0">
        <div className="absolute inset-y-0 left-0 w-px border-l border-dashed border-muted-foreground/30" />
      </div>
      {/* Right = Front cover (user-chosen) */}
      <div className="w-1/2 h-full relative bg-white">
        {card.photoUrl ? (
          <img src={card.photoUrl} alt="Front cover" className="absolute inset-0 w-[85%] h-[85%] m-auto object-contain" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
            </svg>
            <span className="text-[9px] opacity-40">Front cover</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Inside: QR page (left) + inside image with personalization (right) */
function InsidePreview({ card }: { card: CardData }) {
  const isSerif = card.theme.fontStyle === 'serif';
  const fontClass = isSerif ? 'font-serif' : 'font-sans';
  const hasPersonalization = card.recipientName || card.message || card.senderName;

  return (
    <div className="w-full max-w-[480px] mx-auto aspect-[10/7] flex rounded-xl overflow-hidden shadow-lg border border-border/50">
      {/* Left = QR / redeem page */}
      <div className="w-1/2 h-full relative bg-white flex flex-col items-center justify-between p-3">
        <p className="text-[7px] font-bold text-gray-800 text-center leading-tight">
          Learn How To Redeem At CashuCards.com
        </p>

        <div className="flex flex-col items-center gap-1">
          {card.qrDataUrl ? (
            <div className="p-1 border border-gray-200 rounded-md">
              <img src={card.qrDataUrl} alt="QR" className="w-24 h-24" style={{ imageRendering: 'pixelated' }} />
            </div>
          ) : (
            <div className="w-24 h-24 bg-gray-100 rounded-md flex items-center justify-center">
              <span className="text-[7px] text-gray-400">QR Code</span>
            </div>
          )}
          <span className="text-[6px] uppercase tracking-wider text-gray-400 font-semibold">Scan to redeem</span>
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <span>{getUnitSymbol(card.unit)}</span>
            <span>{formatAmount(card.amount, card.unit)}</span>
          </div>
        </div>

        <img
          src={REDEEM_IMAGE_URL}
          alt="Cashu Cards"
          className="w-10 h-auto opacity-80"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      {/* Fold line */}
      <div className="w-px bg-border/80 relative z-10 flex-shrink-0">
        <div className="absolute inset-y-0 left-0 w-px border-l border-dashed border-muted-foreground/30" />
      </div>

      {/* Right = Inside image with personalization */}
      <div className={`w-1/2 h-full relative overflow-hidden ${fontClass}`}>
        {/* Background: user image or themed gradient */}
        {card.insideImageUrl ? (
          <img src={card.insideImageUrl} alt="Inside" className="absolute inset-x-0 top-[4%] mx-auto w-[85%] h-[75%] object-contain bg-white" />
        ) : (
          <div className="absolute inset-0" style={{ background: card.theme.bgGradient }}>
            <div className="absolute inset-0 pointer-events-none" style={{ opacity: card.theme.overlayOpacity }}>
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="prev-dots" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="0.5" fill="currentColor" opacity="0.2" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#prev-dots)" />
              </svg>
            </div>
          </div>
        )}

        {/* Personalization overlay at bottom */}
        {hasPersonalization && (
          <div
            className="absolute bottom-0 inset-x-0 p-2.5 text-center z-10"
            style={{
              background: card.insideImageUrl
                ? 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 60%, transparent 100%)'
                : 'transparent',
              color: card.insideImageUrl ? '#ffffff' : card.theme.textColor,
            }}
          >
            {card.recipientName && (
              <div>
                <span className="text-[6px] opacity-60 tracking-[0.15em] uppercase font-medium">To</span>
                <p className={`text-sm font-bold leading-tight ${isSerif ? 'italic' : ''}`}>{card.recipientName}</p>
              </div>
            )}
            {card.recipientName && card.message && (
              <div className="flex items-center justify-center gap-1.5 opacity-40 my-0.5">
                <div className="w-3 h-px bg-current" />
                <div className="w-1 h-1 rounded-full bg-current" />
                <div className="w-3 h-px bg-current" />
              </div>
            )}
            {card.message && (
              <p className={`text-[8px] leading-relaxed opacity-90 px-1 ${isSerif ? 'italic' : ''}`}>
                &ldquo;{card.message}&rdquo;
              </p>
            )}
            {card.senderName && (
              <div className="pt-0.5">
                <span className="text-[6px] opacity-50 tracking-[0.15em] uppercase font-medium">From</span>
                <p className={`text-[9px] font-semibold ${isSerif ? 'italic' : ''}`}>{card.senderName}</p>
              </div>
            )}
          </div>
        )}

        {/* Centered text when no image and no personalization */}
        {!card.insideImageUrl && !hasPersonalization && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ color: card.theme.textColor }}>
            <span className="text-[9px] opacity-30">Inside page</span>
          </div>
        )}
      </div>
    </div>
  );
}
